import { Box, Button, CloseButton, Flex, Text } from '@chakra-ui/react'
import { INSTALL_PROMPT_DISMISSED_AT } from 'consts'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { canPromptInstall, onInstallPromptChange, showInstallPrompt } from 'utils/install-prompt'
import { canInstallOnIos, isStandalone } from 'utils/pwa'

const DISMISSED_FOR_MS = 30 * 24 * 60 * 60 * 1000

const wasDismissedRecently = () => {
  const dismissedAt = Number(localStorage.getItem(INSTALL_PROMPT_DISMISSED_AT))
  return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISSED_FOR_MS
}

/**
 * Invitation to install the app.
 *
 * Chromium tells us when the app is installable and gives us a dialog to open. iOS has no
 * equivalent — installing is only possible from Safari's share sheet — so there we can do
 * no more than point at it.
 */
export const InstallPrompt = () => {
  const { formatMessage } = useIntl()
  const [dismissed, setDismissed] = useState(wasDismissedRecently)
  const [installable, setInstallable] = useState(canPromptInstall)
  const iosHint = canInstallOnIos()

  useEffect(() => onInstallPromptChange(() => setInstallable(canPromptInstall())), [])

  if (dismissed || isStandalone() || (!installable && !iosHint)) {
    return null
  }

  const dismiss = () => {
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_AT, String(Date.now()))
    setDismissed(true)
  }

  return (
    <Box
      px={4}
      py={3}
      backgroundColor="white"
      borderTopWidth="1px"
      borderTopColor="ctaBlack.200"
      display={{ base: 'block', lg: 'none' }}
      data-testid="install-prompt"
    >
      <Flex maxWidth="1184px" mx="auto" gap={3} alignItems="center">
        <Box flex="1">
          <Text fontSize="sm" fontWeight="500">
            {formatMessage({ id: 'pwa.install.title', defaultMessage: 'Add Wanda to your phone' })}
          </Text>
          {iosHint ? (
            <Text fontSize="xs" color="ctaBlack.800">
              {formatMessage({
                id: 'pwa.install.iosHint',
                defaultMessage: 'Tap the share button and choose "Add to Home Screen".',
              })}
            </Text>
          ) : null}
        </Box>
        {installable ? (
          <Button
            size="sm"
            colorScheme="purple"
            data-testid="install-app"
            onClick={async () => {
              const accepted = await showInstallPrompt()
              if (!accepted) {
                dismiss()
              }
            }}
          >
            {formatMessage({ id: 'pwa.install.action', defaultMessage: 'Install' })}
          </Button>
        ) : null}
        <CloseButton
          size="sm"
          onClick={dismiss}
          aria-label={formatMessage({ id: 'word.close', defaultMessage: 'Close' })}
        />
      </Flex>
    </Box>
  )
}
